import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { GATES } from "@/lib/gates";
import type { GateProgressRow } from "@/lib/gates.functions";

interface Props {
  progress: GateProgressRow[];
  reduceMotion?: boolean;
  onSelect?: (gateN: number) => void;
  selectedGate?: number | null;
}


type NodeState = "completed" | "current" | "future";

/**
 * Illuminated constellation of the 47 Gates.
 * Completed gates glow luminous gold; the current gate breathes slowly;
 * future gates rest as dim outlines. Connections between consecutive
 * completed gates are drawn to reveal the path already walked.
 */
export function GateProgressMap({ progress, reduceMotion = false, onSelect, selectedGate }: Props) {
  const byGate = useMemo(() => {
    const m = new Map<number, GateProgressRow>();
    for (const p of progress) m.set(p.gate_n, p);
    return m;
  }, [progress]);

  // Current gate = smallest gate_n that is not completed AND either
  // (a) has any actions recorded, or (b) is the immediate successor of the
  // highest completed gate. Defaults to 1.
  const currentN = useMemo(() => {
    const completed = new Set(
      progress.filter((p) => p.completed_at).map((p) => p.gate_n),
    );
    // In-progress candidate first
    const inProgress = progress.find(
      (p) => !p.completed_at && (p.actions?.length ?? 0) > 0,
    );
    if (inProgress) return inProgress.gate_n;
    // Otherwise successor of highest completed
    const highest = completed.size
      ? Math.max(...Array.from(completed))
      : 0;
    return Math.min(47, highest + 1);
  }, [progress]);

  const nodes = useMemo(() => {
    const cx = 400,
      cy = 400;
    return GATES.map((g, i) => {
      const t = i / GATES.length;
      const angle = i * 0.6;
      const r = 60 + t * 300;
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      const row = byGate.get(g.n);
      let state: NodeState = "future";
      if (row?.completed_at) state = "completed";
      else if (g.n === currentN) state = "current";
      return { gate: g, x, y, state, actions: row?.actions?.length ?? 0 };
    });
  }, [byGate, currentN]);

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-gradient-to-br from-obsidian via-obsidian to-black">
      <svg
        viewBox="0 0 800 800"
        className="h-full w-full"
        role="img"
        aria-label="The 47 Gates — illuminated constellation of your journey"
      >
        <defs>
          <radialGradient id="gate-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f5d76e" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#f5d76e" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Background thread — every consecutive pair, very faint */}
        {nodes.slice(0, -1).map((n, i) => {
          const next = nodes[i + 1];
          return (
            <line
              key={`bg-${n.gate.n}`}
              x1={n.x}
              y1={n.y}
              x2={next.x}
              y2={next.y}
              stroke="rgba(212,175,55,0.06)"
              strokeWidth={1}
            />
          );
        })}

        {/* Illuminated thread — only between completed → next */}
        {nodes.slice(0, -1).map((n, i) => {
          const next = nodes[i + 1];
          if (n.state !== "completed") return null;
          const bright = next.state === "completed" ? 0.55 : 0.22;
          return (
            <line
              key={`lit-${n.gate.n}`}
              x1={n.x}
              y1={n.y}
              x2={next.x}
              y2={next.y}
              stroke={`rgba(245,215,110,${bright})`}
              strokeWidth={1.5}
            />
          );
        })}

        {nodes.map((n) => {
          const fill =
            n.state === "completed"
              ? "#f5d76e"
              : n.state === "current"
                ? "#fff3c4"
                : "rgba(212,175,55,0.28)";
          const r = n.state === "current" ? 8 : n.state === "completed" ? 6 : 3.5;
          const isSelected = selectedGate === n.gate.n;
          const nodeCircle = (
            <circle
              className="gate-dot"
              cx={n.x}
              cy={n.y}
              r={isSelected ? r + 2 : r}
              fill={fill}
              stroke={isSelected ? "#fff3c4" : n.state === "current" ? "#f5d76e" : "none"}
              strokeWidth={isSelected ? 2 : n.state === "current" ? 1 : 0}
              style={onSelect ? { cursor: "pointer" } : undefined}
            >
              <title>
                Gate {n.gate.n} · {n.gate.title} ·{" "}
                {n.state === "completed"
                  ? "walked"
                  : n.state === "current"
                    ? "current"
                    : "ahead"}
              </title>
            </circle>
          );
          const stateLabel =
            n.state === "completed" ? "walked" : n.state === "current" ? "current" : "ahead";
          const ariaLabel = `Gate ${n.gate.n}, ${n.gate.title}, ${stateLabel}`;
          const focusRingR = Math.max(r + 6, 12);
          if (onSelect) {
            return (
              <g
                key={n.gate.n}
                className="gate-node"
                role="button"
                tabIndex={0}
                aria-label={ariaLabel}
                aria-pressed={isSelected}
                data-gate={n.gate.n}
                onClick={() => onSelect(n.gate.n)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(n.gate.n);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {n.state !== "future" && (
                  <circle cx={n.x} cy={n.y} r={r * 3} fill="url(#gate-glow)">
                    {n.state === "current" && !reduceMotion && (
                      <animate
                        attributeName="r"
                        values={`${r * 3};${r * 4.5};${r * 3}`}
                        dur="3s"
                        repeatCount="indefinite"
                      />
                    )}
                  </circle>
                )}
                {/* Enlarged invisible hit target for touch/keyboard */}
                <circle
                  cx={n.x}
                  cy={n.y}
                  r={Math.max(r + 14, 18)}
                  fill="transparent"
                />
                {/* Focus ring — shown only for :focus-visible via CSS */}
                <circle
                  className="gate-focus-ring"
                  cx={n.x}
                  cy={n.y}
                  r={focusRingR}
                  fill="none"
                  stroke="#fff3c4"
                  strokeWidth={2}
                  strokeDasharray="3 2"
                />
                {nodeCircle}
              </g>
            );
          }

          return (
            <g key={n.gate.n}>
              {n.state !== "future" && (
                <circle cx={n.x} cy={n.y} r={r * 3} fill="url(#gate-glow)">
                  {n.state === "current" && !reduceMotion && (
                    <animate
                      attributeName="r"
                      values={`${r * 3};${r * 4.5};${r * 3}`}
                      dur="3s"
                      repeatCount="indefinite"
                    />
                  )}
                </circle>
              )}
              <Link
                to="/gates/$n"
                params={{ n: String(n.gate.n) }}
                aria-label={ariaLabel}
              >
                {nodeCircle}
              </Link>
            </g>
          );


        })}
      </svg>
    </div>
  );
}
